import { useState, useEffect } from 'react'

export interface Company {
  id: string
  name: string
  description: string | null
  website: string | null
  logoUrl: string | null
  createdAt: string
  updatedAt: string
  // Extended fields from Epic 18 Story 2
  employeeCount: number | null
  revenueRange: string | null
  businessBranches: string | null
  aiPainPoints: string | null
  aiAdoptionStage: string | null
  isOnboarded: boolean | null
  isDemo: boolean | null
  _count: {
    users: number
  }
}

export interface CompaniesResponse {
  companies: Company[]
}

export interface CompanyFilters {
  search?: string
  revenueRange?: string[]
  aiAdoptionStage?: string[]
  minEmployees?: number
  maxEmployees?: number
  isDemo?: boolean
  isOnboarded?: boolean
}

export interface UseCompaniesResult {
  companies: Company[]
  loading: boolean
  error: string | null
  refetchCompanies: () => void
}

export function useCompanies(filters?: CompanyFilters): UseCompaniesResult {
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCompanies = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build query parameters
      const params = new URLSearchParams()
      if (filters?.search) params.append('search', filters.search)
      if (filters?.revenueRange) filters.revenueRange.forEach(r => params.append('revenueRange', r))
      if (filters?.aiAdoptionStage) filters.aiAdoptionStage.forEach(s => params.append('aiAdoptionStage', s))
      if (filters?.minEmployees !== undefined) params.append('minEmployees', filters.minEmployees.toString())
      if (filters?.maxEmployees !== undefined) params.append('maxEmployees', filters.maxEmployees.toString())
      if (filters?.isDemo !== undefined) params.append('isDemo', filters.isDemo.toString())
      if (filters?.isOnboarded !== undefined) params.append('isOnboarded', filters.isOnboarded.toString())

      const queryString = params.toString()
      const url = `/api/admin/companies${queryString ? `?${queryString}` : ''}`

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to fetch companies (${response.status})`)
      }

      const data: CompaniesResponse = await response.json()
      setCompanies(data.companies)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch companies'
      setError(errorMessage)
      console.error('Error fetching companies:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCompanies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.search, filters?.revenueRange, filters?.aiAdoptionStage, filters?.minEmployees, filters?.maxEmployees, filters?.isDemo, filters?.isOnboarded])

  return {
    companies,
    loading,
    error,
    refetchCompanies: fetchCompanies
  }
}
